"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Wrench,
  Clock,
  ArrowLeft,
  Bell,
  RefreshCw,
} from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { ShimmerBar } from "@/components/site/StatePrimitives";
import {
  seoMeta,
  seoLinks,
  jsonLd,
  breadcrumbLd,
  organizationLd,
  itemListLd,
  SITE,
} from "@/lib/seo/seo";

const STATUS_PATH = "/status";
const STATUS_TITLE = "وضعیت سرویس | ترندی اسمارت";
const STATUS_DESC =
  "پایش زنده‌ی وضعیت API، داشبورد، Instagram، WhatsApp، Telegram، SMS، Trendi AI و پرداخت — به همراه تاریخچه ۶۰ روزه و رویدادهای اخیر.";
const STATUS_KEYWORDS = [
  "وضعیت ترندی اسمارت",
  "Trendi Smart status",
  "uptime",
  "آپ‌تایم",
  "incidents",
  "service health",
  "اختلال سرویس",
];

const STATUS_SERVICES = [
  { name: "API هسته", description: "REST + WebSocket" },
  { name: "داشبورد", description: "وب اپلیکیشن کاربران" },
  { name: "Instagram Webhooks", description: "DM، کامنت، استوری" },
  { name: "WhatsApp Cloud API", description: "پیام‌های ورودی و خروجی" },
  { name: "Telegram Bot", description: "ربات‌ها و گروه‌ها" },
  { name: "ارسال SMS", description: "پنل‌های پیامک" },
  { name: "Trendi AI", description: "موتور پاسخ‌گویی" },
  { name: "پرداخت", description: "زرین‌پال + IDPay" },
];

type Health = "operational" | "degraded" | "outage" | "maintenance";

type Service = {
  id: string;
  name: string;
  desc: string;
  health: Health;
  uptime: number; // 0-100
  history: Health[]; // 60 days
};

type Incident = {
  id: string;
  title: string;
  severity: "minor" | "major" | "critical" | "maintenance";
  status: "investigating" | "identified" | "monitoring" | "resolved";
  startedAt: string;
  resolvedAt?: string;
  service: string;
  updates: { at: string; text: string; status: Incident["status"] }[];
};

const TONES: Record<
  Health,
  {
    bg: string;
    text: string;
    ring: string;
    bar: string;
    label: string;
    Icon: React.ComponentType<{ size?: number; className?: string }>;
  }
> = {
  operational: {
    bg: "bg-success/15",
    text: "text-success",
    ring: "ring-success/40",
    bar: "bg-success",
    label: "عملیاتی",
    Icon: CheckCircle2,
  },
  degraded: {
    bg: "bg-secondary-main/15",
    text: "text-secondary-main",
    ring: "ring-secondary-main/40",
    bar: "bg-secondary-main",
    label: "افت کیفیت",
    Icon: AlertTriangle,
  },
  outage: {
    bg: "bg-error/15",
    text: "text-error",
    ring: "ring-error/40",
    bar: "bg-error",
    label: "قطع",
    Icon: AlertCircle,
  },
  maintenance: {
    bg: "bg-primary-main/15",
    text: "text-primary-main",
    ring: "ring-primary-main/40",
    bar: "bg-primary-main",
    label: "تعمیرات",
    Icon: Wrench,
  },
};

function genHistory(seed: number, biased: Health = "operational"): Health[] {
  const arr: Health[] = [];
  let s = seed;
  for (let i = 0; i < 60; i++) {
    s = (s * 9301 + 49297) % 233280;
    const r = s / 233280;
    if (biased !== "operational" && i > 55) arr.push(biased);
    else if (r < 0.92) arr.push("operational");
    else if (r < 0.96) arr.push("degraded");
    else if (r < 0.99) arr.push("maintenance");
    else arr.push("outage");
  }
  return arr;
}

const BASE_SERVICES: Service[] = [
  {
    id: "api",
    name: "API هسته",
    desc: "REST + WebSocket",
    health: "operational",
    uptime: 99.99,
    history: genHistory(12),
  },
  {
    id: "dash",
    name: "داشبورد",
    desc: "وب اپلیکیشن کاربران",
    health: "operational",
    uptime: 99.98,
    history: genHistory(34),
  },
  {
    id: "ig",
    name: "Instagram Webhooks",
    desc: "DM، کامنت، استوری",
    health: "operational",
    uptime: 99.93,
    history: genHistory(56),
  },
  {
    id: "wa",
    name: "WhatsApp Cloud API",
    desc: "پیام‌های ورودی و خروجی",
    health: "operational",
    uptime: 99.96,
    history: genHistory(78),
  },
  {
    id: "tg",
    name: "Telegram Bot",
    desc: "ربات‌ها و گروه‌ها",
    health: "operational",
    uptime: 99.99,
    history: genHistory(99),
  },
  {
    id: "sms",
    name: "ارسال SMS",
    desc: "پنل‌های پیامک",
    health: "operational",
    uptime: 99.91,
    history: genHistory(123),
  },
  {
    id: "ai",
    name: "Trendi AI",
    desc: "موتور پاسخ‌گویی",
    health: "operational",
    uptime: 99.95,
    history: genHistory(145),
  },
  {
    id: "pay",
    name: "پرداخت",
    desc: "زرین‌پال + IDPay",
    health: "operational",
    uptime: 99.97,
    history: genHistory(167),
  },
];

const RECENT_INCIDENTS: Incident[] = [
  {
    id: "i1",
    title: "تأخیر در دریافت webhook اینستاگرام",
    severity: "minor",
    status: "resolved",
    service: "Instagram Webhooks",
    startedAt: "۱۴۰۴/۰۲/۱۸ - ۱۴:۲۲",
    resolvedAt: "۱۴۰۴/۰۲/۱۸ - ۱۵:۰۴",
    updates: [
      {
        at: "۱۵:۰۴",
        text: "مشکل برطرف شد. متوسط تأخیر به کمتر از ۲ ثانیه بازگشت.",
        status: "resolved",
      },
      { at: "۱۴:۴۰", text: "علت در صف پردازش webhook شناسایی شد.", status: "monitoring" },
      { at: "۱۴:۲۲", text: "تیم ما در حال بررسی افزایش تأخیر است.", status: "investigating" },
    ],
  },
  {
    id: "i2",
    title: "تعمیرات برنامه‌ریزی‌شده پایگاه داده",
    severity: "maintenance",
    status: "resolved",
    service: "API هسته",
    startedAt: "۱۴۰۴/۰۲/۱۲ - ۰۳:۰۰",
    resolvedAt: "۱۴۰۴/۰۲/۱۲ - ۰۴:۰۰",
    updates: [
      { at: "۰۴:۰۰", text: "تعمیرات با موفقیت تمام شد.", status: "resolved" },
      { at: "۰۳:۰۰", text: "آغاز پنجره تعمیرات (۱ ساعت).", status: "monitoring" },
    ],
  },
];

type Scenario =
  | "all-good"
  | "loading"
  | "minor-incident"
  | "major-outage"
  | "maintenance-active"
  | "investigating-now"
  | "history-clean"
  | "no-incidents";

export default function StatusPage() {
  const [scenario, setScenario] = useState<Scenario>("loading");
  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    if (scenario !== "loading") return;
    const t = setTimeout(() => setScenario("all-good"), 1000);
    return () => clearTimeout(t);
  }, [scenario]);

  useEffect(() => {
    setNow(new Date());
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const services = useMemo<Service[]>(() => {
    const cl = BASE_SERVICES.map((s) => ({ ...s, history: [...s.history] }));
    if (scenario === "minor-incident") {
      cl[2] = { ...cl[2], health: "degraded", uptime: 99.42, history: genHistory(56, "degraded") };
    } else if (scenario === "major-outage") {
      cl[0] = { ...cl[0], health: "outage", uptime: 97.12, history: genHistory(12, "outage") };
      cl[1] = { ...cl[1], health: "degraded", uptime: 98.4, history: genHistory(34, "degraded") };
      cl[6] = { ...cl[6], health: "degraded", uptime: 98.9, history: genHistory(145, "degraded") };
    } else if (scenario === "maintenance-active") {
      cl[5] = {
        ...cl[5],
        health: "maintenance",
        uptime: 99.5,
        history: genHistory(123, "maintenance"),
      };
    } else if (scenario === "investigating-now") {
      cl[3] = { ...cl[3], health: "degraded", uptime: 99.1, history: genHistory(78, "degraded") };
    } else if (scenario === "history-clean") {
      return cl.map((s) => ({
        ...s,
        history: Array.from({ length: 60 }, () => "operational" as Health),
      }));
    }
    return cl;
  }, [scenario]);

  const overall: Health = useMemo(() => {
    if (services.some((s) => s.health === "outage")) return "outage";
    if (services.some((s) => s.health === "degraded")) return "degraded";
    if (services.some((s) => s.health === "maintenance")) return "maintenance";
    return "operational";
  }, [services]);

  const activeIncidents: Incident[] = useMemo(() => {
    if (scenario === "minor-incident")
      return [
        {
          id: "live1",
          title: "افت کیفیت در دریافت پیام‌های اینستاگرام",
          severity: "minor",
          status: "monitoring",
          service: "Instagram Webhooks",
          startedAt: "همین الان",
          updates: [
            { at: "اکنون", text: "تأخیر کاهش یافته، در حال پایش.", status: "monitoring" },
            { at: "−۱۲ دقیقه", text: "علت افزایش بار شناسایی شد.", status: "identified" },
            { at: "−۲۵ دقیقه", text: "بررسی آغاز شد.", status: "investigating" },
          ],
        },
      ];
    if (scenario === "major-outage")
      return [
        {
          id: "live2",
          title: "قطعی API هسته و افت داشبورد",
          severity: "critical",
          status: "investigating",
          service: "API هسته",
          startedAt: "−۸ دقیقه",
          updates: [
            { at: "اکنون", text: "تیم on-call فعال شده، در حال شناسایی.", status: "investigating" },
          ],
        },
      ];
    if (scenario === "investigating-now")
      return [
        {
          id: "live3",
          title: "بررسی تأخیر ارسال WhatsApp",
          severity: "minor",
          status: "investigating",
          service: "WhatsApp Cloud API",
          startedAt: "−۳ دقیقه",
          updates: [{ at: "اکنون", text: "در حال جمع‌آوری متریک‌ها.", status: "investigating" }],
        },
      ];
    if (scenario === "maintenance-active")
      return [
        {
          id: "live4",
          title: "تعمیرات برنامه‌ریزی‌شده SMS Gateway",
          severity: "maintenance",
          status: "monitoring",
          service: "ارسال SMS",
          startedAt: "همین الان",
          updates: [
            { at: "اکنون", text: "پنجره تعمیرات ۴۵ دقیقه‌ای آغاز شد.", status: "monitoring" },
          ],
        },
      ];
    return [];
  }, [scenario]);

  const past = scenario === "no-incidents" ? [] : RECENT_INCIDENTS;

  return (
    <SiteShell>
      <Hero
        overall={overall}
        now={now}
        loading={scenario === "loading"}
        onRefresh={() => setNow(new Date())}
      />

      {activeIncidents.length > 0 && <ActiveIncidents items={activeIncidents} />}

      {scenario === "loading" ? <ServicesSkel /> : <ServicesList services={services} />}

      <UptimeSummary services={services} loading={scenario === "loading"} />

      <PastIncidents items={past} loading={scenario === "loading"} />

      <CTA />
    </SiteShell>
  );
}

function Hero({
  overall,
  now,
  loading,
  onRefresh,
}: {
  overall: Health;
  now: Date | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  const t = TONES[overall];
  const Icon = t.Icon;
  return (
    <section className="mx-auto max-w-7xl px-5 pt-12 pb-8 sm:px-6 sm:pt-20">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`relative overflow-hidden rounded-3xl border bg-card p-6 sm:p-8 ${
          overall === "operational"
            ? "border-success/30"
            : overall === "outage"
              ? "border-error/40"
              : overall === "degraded"
                ? "border-secondary-main/40"
                : "border-primary-main/40"
        }`}
      >
        <div className={`absolute inset-x-0 top-0 h-1 ${t.bar}`} />
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <div
              className={`relative flex h-14 w-14 items-center justify-center rounded-2xl ${t.bg} ${t.text} ring-4 ${t.ring}`}
            >
              <Icon size={24} />
              {overall === "operational" && (
                <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-success" />
                </span>
              )}
            </div>
            <div>
              <div className="text-[12px] font-bold text-muted-foreground">وضعیت کلی پلتفرم</div>
              <div className={`mt-1 text-2xl font-black ${t.text}`}>
                {overall === "operational"
                  ? "همه سرویس‌ها عملیاتی هستند"
                  : overall === "degraded"
                    ? "افت کیفیت در برخی سرویس‌ها"
                    : overall === "outage"
                      ? "قطعی فعال در یک یا چند سرویس"
                      : "تعمیرات در حال انجام"}
              </div>
              <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Clock size={11} /> آخرین بروزرسانی {now ? now.toLocaleTimeString("fa-IR") : "—"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-background px-3.5 py-2 text-[12px] font-black text-foreground hover:bg-muted"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> بروزرسانی
            </button>
            <button className="inline-flex items-center gap-1.5 rounded-xl bg-primary-main px-3.5 py-2 text-[12px] font-black text-white hover:bg-primary-400">
              <Bell size={12} /> اشتراک رویدادها
            </button>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function ActiveIncidents({ items }: { items: Incident[] }) {
  return (
    <section className="mx-auto max-w-7xl px-5 pb-8 sm:px-6">
      <div className="space-y-3">
        {items.map((it) => {
          const sev =
            it.severity === "critical"
              ? "border-error/40 bg-error/5"
              : it.severity === "major"
                ? "border-error/30 bg-error/5"
                : it.severity === "maintenance"
                  ? "border-primary-main/30 bg-primary-main/5"
                  : "border-secondary-main/30 bg-secondary-main/5";
          const sevTxt =
            it.severity === "critical"
              ? "بحرانی"
              : it.severity === "major"
                ? "مهم"
                : it.severity === "maintenance"
                  ? "تعمیرات"
                  : "جزئی";
          return (
            <motion.div
              key={it.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl border p-5 ${sev}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-[10px] font-black text-foreground">
                      {sevTxt}
                    </span>
                    <span className="text-[10.5px] text-muted-foreground">
                      {it.service} · شروع {it.startedAt}
                    </span>
                  </div>
                  <h3 className="text-[15px] font-black text-foreground">{it.title}</h3>
                </div>
                <span className="shrink-0 rounded-full bg-foreground/10 px-2 py-0.5 text-[10.5px] font-black text-foreground">
                  {it.status === "investigating"
                    ? "در حال بررسی"
                    : it.status === "identified"
                      ? "شناسایی شد"
                      : it.status === "monitoring"
                        ? "در حال پایش"
                        : "حل شد"}
                </span>
              </div>
              <ol className="relative mt-4 space-y-2 border-r border-dashed border-border pr-4">
                {it.updates.map((u, k) => (
                  <li key={k} className="relative">
                    <span className="absolute -right-[21px] top-1.5 h-2 w-2 rounded-full bg-primary-main" />
                    <div className="text-[10.5px] text-muted-foreground">{u.at}</div>
                    <div className="text-[12px] text-foreground">{u.text}</div>
                  </li>
                ))}
              </ol>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function ServicesList({ services }: { services: Service[] }) {
  return (
    <section className="mx-auto max-w-7xl px-5 pb-8 sm:px-6">
      <div className="overflow-hidden rounded-3xl border border-border bg-card">
        <div className="border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <Activity size={15} className="text-primary-main" />
            <h2 className="text-[15px] font-black text-foreground">سرویس‌ها (۶۰ روز گذشته)</h2>
          </div>
        </div>
        <div className="divide-y divide-border">
          {services.map((s, i) => {
            const t = TONES[s.health];
            const Icon = t.Icon;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                className="grid gap-3 p-4 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-4 sm:p-5"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${t.bg} ${t.text}`}
                  >
                    <Icon size={15} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-black text-foreground">{s.name}</div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">{s.desc}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-end gap-[2px]" aria-hidden>
                    {s.history.map((h, k) => (
                      <span
                        key={k}
                        title={h}
                        className={`h-6 w-1 rounded-sm ${TONES[h].bar} opacity-90 transition-transform hover:scale-y-110`}
                      />
                    ))}
                  </div>
                  <div className="min-w-[70px] text-left">
                    <div className={`text-[13px] font-black ${t.text}`}>{s.uptime.toFixed(2)}%</div>
                    <div className="text-[10px] text-muted-foreground">uptime</div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServicesSkel() {
  return (
    <section className="mx-auto max-w-7xl px-5 pb-8 sm:px-6">
      <div className="overflow-hidden rounded-3xl border border-border bg-card">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="grid items-center gap-3 border-b border-border p-5 sm:grid-cols-[1fr_auto]"
          >
            <div className="flex items-center gap-3">
              <ShimmerBar className="h-9 w-9 rounded-xl" />
              <div className="space-y-2">
                <ShimmerBar className="h-3 w-32" />
                <ShimmerBar className="h-2.5 w-24" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-end gap-[2px]">
                {Array.from({ length: 60 }).map((_, k) => (
                  <ShimmerBar key={k} className="h-6 w-1 rounded-sm" />
                ))}
              </div>
              <ShimmerBar className="h-4 w-14" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function UptimeSummary({ services, loading }: { services: Service[]; loading: boolean }) {
  const periods = [
    {
      label: "۲۴ ساعت",
      v: services.reduce((a, s) => a + Math.min(100, s.uptime + 0.01), 0) / services.length,
    },
    { label: "۷ روز", v: services.reduce((a, s) => a + s.uptime, 0) / services.length },
    {
      label: "۳۰ روز",
      v: services.reduce((a, s) => a + Math.max(0, s.uptime - 0.05), 0) / services.length,
    },
    {
      label: "۹۰ روز",
      v: services.reduce((a, s) => a + Math.max(0, s.uptime - 0.1), 0) / services.length,
    },
  ];
  return (
    <section className="mx-auto max-w-7xl px-5 pb-8 sm:px-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {periods.map((p) => (
          <div key={p.label} className="rounded-2xl border border-border bg-card p-5">
            <div className="text-[11.5px] text-muted-foreground">{p.label} گذشته</div>
            {loading ? (
              <ShimmerBar className="mt-2 h-7 w-24" />
            ) : (
              <div className="mt-1 text-2xl font-black text-success">{p.v.toFixed(2)}%</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function PastIncidents({ items, loading }: { items: Incident[]; loading: boolean }) {
  return (
    <section className="mx-auto max-w-7xl px-5 pb-12 sm:px-6">
      <div className="rounded-3xl border border-border bg-card p-5 sm:p-7">
        <h2 className="mb-4 flex items-center gap-2 text-[15px] font-black text-foreground">
          <Clock size={15} className="text-primary-main" /> رویدادهای گذشته
        </h2>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-2 rounded-2xl border border-border bg-background p-4">
                <ShimmerBar className="h-4 w-2/3" />
                <ShimmerBar className="h-3 w-1/3" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-background/50 p-8 text-center">
            <CheckCircle2 size={22} className="mx-auto mb-2 text-success" />
            <div className="text-[13px] font-black text-foreground">
              هیچ رویدادی در ۹۰ روز گذشته ثبت نشده
            </div>
            <div className="mt-1 text-[11.5px] text-muted-foreground">عملکرد پایدار پلتفرم.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.id} className="rounded-2xl border border-border bg-background p-4">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-black ${
                      it.severity === "maintenance"
                        ? "bg-primary-main/15 text-primary-main"
                        : "bg-secondary-main/15 text-secondary-main"
                    }`}
                  >
                    {it.severity === "maintenance" ? "تعمیرات" : "رویداد"}
                  </span>
                  <span className="rounded-full bg-success/15 px-2 py-0.5 text-[10px] font-black text-success">
                    حل شده
                  </span>
                  <span className="text-[10.5px] text-muted-foreground">{it.service}</span>
                </div>
                <div className="text-[13px] font-black text-foreground">{it.title}</div>
                <div className="mt-1 text-[10.5px] text-muted-foreground">
                  {it.startedAt} → {it.resolvedAt}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="mx-auto max-w-5xl px-5 pb-24 sm:px-6">
      <div className="rounded-3xl border border-primary-main/30 bg-gradient-to-br from-primary-main/10 via-card to-secondary-main/10 p-8 text-center sm:p-12">
        <Bell size={20} className="mx-auto mb-3 text-secondary-main" />
        <h2 className="mb-3 text-2xl font-black text-foreground sm:text-3xl">
          از رویدادها مطلع شو
        </h2>
        <p className="mx-auto mb-6 max-w-md text-[13px] text-muted-foreground">
          ایمیل، SMS یا RSS — هرگز یک قطعی را از دست نده.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <button className="inline-flex items-center gap-2 rounded-xl bg-primary-main px-6 py-3 text-[13px] font-black text-white hover:bg-primary-400">
            اشتراک رویدادها <ArrowLeft size={14} />
          </button>
          <Link
            href="/security"
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-6 py-3 text-[13px] font-black text-foreground hover:bg-muted"
          >
            امنیت ما
          </Link>
        </div>
      </div>
    </section>
  );
}
